import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { MapPin, Loader2, ChevronLeft, Search, CheckCircle2, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/errors";
import { useTranslation } from "react-i18next";

const NOMINATIM_SEARCH = "https://nominatim.openstreetmap.org/search";

export default function BookService() {
  const { t } = useTranslation();
  const { serviceId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [service, setService] = useState(null);
  const [coords, setCoords] = useState(null);
  const [fullAddress, setFullAddress] = useState("");
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const suggestRef = useRef(null);

  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [wsStatus, setWsStatus] = useState(null);
  const wsRef = useRef(null);

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    address: "",
    amount: 0,
    notes: "",
  });

  useEffect(() => {
    if (!user) return;
    api.get("/services").then((r) => {
      const s = r.data.find((x) => x.id === serviceId);
      if (!s) {
        toast.error("Service not found");
        nav("/services");
        return;
      }
      setService(s);
      setForm({
        full_name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        amount: s.base_price,
        notes: "",
      });
    });
  }, [serviceId, user, nav]);

  useEffect(() => {
    if (!confirmedBooking || !user) return;
    const ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL?.replace(/^http/, "ws")}/api/ws/${user.id}`);
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "booking_update" && data.booking?.id === confirmedBooking.id) {
          setWsStatus(data.booking.status);
          if (["accepted", "on_the_way", "completed", "cancelled"].includes(data.booking.status)) {
            ws.close();
          }
        }
      } catch {}
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [confirmedBooking, user]);

  // Click outside to close suggestions
  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const searchAddress = useCallback((query) => {
    if (!query.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
    setSearching(true);
    fetch(`${NOMINATIM_SEARCH}?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`)
      .then(r => r.json())
      .then(data => {
        setSuggestions(data.map(d => ({
          label: d.display_name,
          lat: parseFloat(d.lat),
          lng: parseFloat(d.lon),
        })));
        setShowSuggestions(true);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setSearching(false));
  }, []);

  const handleAddressChange = (val) => {
    setForm(prev => ({ ...prev, address: val }));
    // Clear coords when user types manually
    setCoords(null);
    setFullAddress("");
    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAddress(val), 400);
  };

  const selectSuggestion = (s) => {
    setForm(prev => ({ ...prev, address: s.label }));
    setCoords({ lat: s.lat, lng: s.lng });
    setFullAddress(s.label);
    setShowSuggestions(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const detectLocation = () => {
    setLoadingLoc(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          );
          const data = await res.json();
          const address = data.address;
          const full =
            [address?.road, address?.suburb, address?.city || address?.town, address?.state]
              .filter(Boolean).join(", ") || "Your location";
          setFullAddress(full);
          setForm(prev => ({ ...prev, address: full }));
        } catch {
          setFullAddress("Location detected");
        }
        setLoadingLoc(false);
        toast.success("Location set");
      },
      () => {
        toast.error("Location denied. Type your address manually and select from suggestions.");
        setLoadingLoc(false);
      }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!coords) {
      toast.error("Please detect your location or select an address from the suggestions");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service_id: serviceId,
        full_name: form.full_name,
        phone: form.phone,
        address: form.address,
        amount: parseFloat(form.amount),
        notes: form.notes,
        lat: coords.lat,
        lng: coords.lng,
      };
      const { data } = await api.post("/bookings", payload);
      setConfirmedBooking(data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Booking failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!service) return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin h-8 w-8 border-2 border-[#1C4532] border-t-transparent rounded-full" />
        <p className="text-sm text-[#646964] animate-pulse">{t("common.loading")}</p>
      </div>
    </div>
  );

  return (
    <><Seo title="Book a Service" description="Book trusted home service professionals on Universal Tree. Schedule AC repair, plumbing, cleaning, and more." noindex />
    <div className="min-h-screen bg-[#F5F7F9]">
      <div className="hidden md:block"><Navbar /></div>
      <div className="mobile-top-bar flex items-center gap-3 px-4 py-3 md:hidden">
        <button onClick={() => nav(-1)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-[#F3F1ED]">
          <ChevronLeft size={22} />
        </button>
        <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#050706] shadow-[0_4px_12px_rgba(28,69,50,0.18)]">
           <img src="/theuniversaltree-logo.jpeg" alt="" className="h-full w-full object-cover rounded-full" />
        </span>
        <h1 className="font-display text-base font-semibold text-[#1A1D1A]">Universal Tree</h1>
      </div>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-2 gap-10">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("booking.title")}</h1>
          <p className="text-gray-500 mt-2">{service.name}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
              <div>
                <label className="text-sm text-gray-500">{t("booking.form.fullName")}</label>
                <Input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className="mt-2 h-12 rounded-xl" />
              </div>
              <div>
                <label className="text-sm text-gray-500">{t("booking.form.phone")}</label>
                <Input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-2 h-12 rounded-xl" />
              </div>
              <div ref={suggestRef} className="relative">
                <label className="text-sm text-gray-500">{t("booking.form.address")}</label>
                <Input required value={form.address} onChange={e => handleAddressChange(e.target.value)} placeholder="Start typing your address..." className="mt-2 h-12 rounded-xl" />
                {searching && <Search size={16} className="absolute right-4 top-[44px] text-gray-400 animate-pulse" />}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-60 overflow-y-auto">
                    {suggestions.map((s, i) => (
                      <button key={i} type="button" onClick={() => selectSuggestion(s)} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors">
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-500">{t("booking.form.amount")}</label>
                <Input type="number" value={form.amount} disabled className="mt-2 h-12 rounded-xl opacity-80 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-sm text-gray-500">{t("booking.form.notes")}</label>
                <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-xl" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border p-4 flex items-center justify-between">
              <Button type="button" onClick={detectLocation} disabled={loadingLoc} variant="outline" className="rounded-xl">
                {loadingLoc ? <Loader2 className="animate-spin mr-2" size={16} /> : <MapPin className="mr-2" size={16} />}
                {t("booking.detectLocation")}
              </Button>
              {coords && (
                <div className="bg-[#1C4532]/10 px-3 py-2 rounded-xl text-sm text-[#1C4532] max-w-[220px] text-right font-medium">
                  <MapPin size={14} className="inline mr-1" />{fullAddress}
                </div>
              )}
              {!coords && (
                <div className="text-xs text-gray-400 text-right max-w-[220px]">
                  Required — use Detect Location or select from address suggestions
                </div>
              )}
            </div>

            <Button type="submit" disabled={submitting} className="w-full h-14 rounded-2xl bg-black hover:bg-gray-900 text-white text-lg font-medium shadow-md">
              {submitting ? t("booking.submitting") : t("booking.confirm")}
            </Button>
          </form>
        </div>

        <aside className="sticky top-24 h-fit">
          <div className="bg-white rounded-2xl shadow-md border overflow-hidden">
            <img src={service.image_url} alt="" className="h-52 w-full object-cover" />
            <div className="p-6 space-y-5">
              <div>
                <p className="text-sm text-gray-500">{service.category}</p>
                <h2 className="text-xl font-semibold mt-1">{service.name}</h2>
                <p className="text-sm text-gray-500 mt-2">{service.description}</p>
              </div>
              <div className="flex justify-between items-center pt-4 border-t">
                <span className="text-sm text-gray-500">{t("booking.startingPrice")}</span>
                <span className="text-2xl font-semibold">₹{service.base_price}</span>
              </div>
              <div className="pt-4 border-t">
                <h3 className="text-sm font-semibold mb-2">{t("booking.included.title")}</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>✔ {t("booking.included.item1")}</li>
                  <li>✔ {t("booking.included.item2")}</li>
                  <li>✔ {t("booking.included.item3")}</li>
                </ul>
              </div>
              <div className="pt-4 border-t">
                <h3 className="text-sm font-semibold mb-2">{t("booking.before.title")}</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• {t("booking.before.item1")}</li>
                  <li>• {t("booking.before.item2")}</li>
                  <li>• {t("booking.before.item3")}</li>
                </ul>
              </div>
              <div className="pt-4 border-t">
                <h3 className="text-sm font-semibold mb-2">{t("booking.terms.title")}</h3>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>• {t("booking.terms.item1")}</li>
                  <li>• {t("booking.terms.item2")}</li>
                  <li>• {t("booking.terms.item3")}</li>
                </ul>
              </div>
              <div className="bg-gray-50 border rounded-xl p-3 text-sm text-gray-700">
                🙏 {t("booking.respect")}
              </div>
            </div>
          </div>
        </aside>
      </main>

      {/* ── Booking confirmation overlay ── */}
      {confirmedBooking && (
        <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col items-center justify-center p-6 animate-in fade-in">
          <div className="w-full max-w-sm text-center">
            <div className="w-16 h-16 rounded-full bg-[#22C55E]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-[#22C55E]" />
            </div>
            <h2 className="text-2xl font-semibold text-[#1A1D1A] mb-2">Booking Confirmed!</h2>
            <p className="text-sm text-[#646964] mb-1">{service.name}</p>
            <p className="text-xs text-[#94A3B8] font-mono mb-6">#{confirmedBooking.id}</p>

            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-4 mb-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#94A3B8]">Amount</span>
                <span className="font-semibold text-[#1A1D1A]">₹{confirmedBooking.amount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#94A3B8]">Address</span>
                <span className="font-medium text-[#1A1D1A] text-right max-w-[180px] truncate">{confirmedBooking.address}</span>
              </div>
              <div className="border-t border-[#F3F1ED] pt-3 flex items-center justify-between text-sm">
                <span className="text-[#94A3B8]">Status</span>
                <span className={`font-semibold flex items-center gap-1.5 ${wsStatus === "cancelled" ? "text-red-500" : wsStatus === "accepted" ? "text-[#1C4532]" : "text-[#D9734E]"}`}>
                  {!wsStatus && <><Loader2 size={14} className="animate-spin" /> Finding a pro...</>}
                  {wsStatus === "accepted" && <><CheckCircle2 size={14} /> Pro assigned!</>}
                  {wsStatus === "cancelled" && "No workers available"}
                  {wsStatus && !["accepted", "cancelled"].includes(wsStatus) && wsStatus.replace(/_/g, " ")}
                </span>
              </div>
            </div>

            {!wsStatus && (
              <div className="flex items-center gap-2 justify-center text-xs text-[#94A3B8] mb-6">
                <Clock size={14} /> Contacting nearby pros...
              </div>
            )}

            <div className="flex gap-3">
              {wsStatus !== "accepted" && (
                <Button onClick={() => { nav("/dashboard"); }} variant="outline" className="flex-1 rounded-xl h-12 text-sm">
                  View Dashboard
                </Button>
              )}
              {wsStatus === "accepted" && (
                <Button onClick={() => { nav("/dashboard"); }} className="flex-1 rounded-xl h-12 text-sm bg-[#1C4532] hover:bg-[#143324] text-white">
                  Track Your Pro
                </Button>
              )}
              {wsStatus === "cancelled" && (
                <Button onClick={() => { setConfirmedBooking(null); setWsStatus(null); }} className="flex-1 rounded-xl h-12 text-sm bg-[#D9734E] hover:bg-[#C26240] text-white">
                  <RefreshCw size={16} className="mr-2" /> Retry Booking
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}